// src/config/tuning.ts — every gameplay number lives here (SPEC Appendix A).
// Angles are stored in degrees ONLY in this file and converted at the edge; all logic is radians.
// Lengths are metres, speeds m/s, masses kg, forces newtons, times seconds unless suffixed otherwise.

export const SIM = {
  FIXED_DT: 1 / 120, // s — simulation timestep
  MAX_FRAME_DT: 0.25, // s — clamp on real frame delta, survives tab-switch stalls
  DETECTION_HZ: 10,
  TELEMETRY_HZ: 20,
  DEFAULT_SEED: 0x5eed0001, // master PRNG seed when none is given on the URL
} as const;

export const ATMO = {
  RHO_0: 1.225,
  SCALE_HEIGHT_M: 8500,
  G: 9.80665,
  TURB_SCALE: 0.4,
  TURB_FREQ: 0.15,
  GROUND_TURB_ALT_M: 200,
} as const;

export const AIRFRAME_DEFAULT = {
  massKg: 210,
  wingAreaM2: 2.4,
  wingSpanM: 3.6,
  cl0: 0.08,
  clAlpha: 4.9,
  clMax: 1.35,
  alphaStallDeg: 14,
  cd0: 0.031,
  oswaldE: 0.78,
  cyBeta: -0.28,
  /**
   * Maximum thrust, newtons. **Deviates from SPEC Appendix A, which says 1250.**
   *
   * Appendix A's own numbers contradict each other: 1250 N against `cd0` 0.031 and 2.4 m² of wing
   * balances drag at 153 m/s, which is 33% past the `vneMs` of 115 that the same table sets. The
   * aircraft could therefore destroy itself in 34 seconds of straight-and-level flight at full
   * throttle, with no input from the player and nothing shooting at it — and because a flameout
   * zeroes the endurance readout, the failure surfaced as `ENDUR 0:00` beside a full tank, which
   * blames the fuel system for a structural death.
   *
   * Thrust is the number that moved rather than V_NE, because §3.2.4 lists V_NE as a limit the
   * player manages, and a limit the aircraft crosses unaided is not a limit. At 620 N the ceiling
   * in level flight is 113 m/s — close enough to V_NE that the warning is live, short enough that
   * reaching it takes a deliberate dive. Climb rate falls from 24 m/s to 7 m/s, which is the real
   * cost of the change and is why it is written down here.
   */
  thrustMaxN: 620,
  thrustLagS: 0.8,
  fuelKg: 55,
  fuelBurnKgS: 0.055,
  rollAuthority: 4.6,
  pitchAuthority: 3.1,
  yawAuthority: 1.4,
  rollDamp: 2.4,
  pitchDamp: 3.0,
  yawDamp: 2.2,
  gLimitPos: 6.0,
  gLimitNeg: -3.0,
  gFailure: 8.0,
  vneMs: 115,
  vStallMs: 26,
  vCruiseMs: 78,
  inertia: [42, 88, 120] as [number, number, number],
  irSignature: 1.0,
  rcsM2: 0.35,
} as const;

/**
 * Flight-model shaping constants (SPEC §3.2) that Appendix A leaves implicit.
 *
 * A note on units, because it is the single most likely source of a silent bug here: the
 * `*Authority` and `*Damp` numbers in AIRFRAME_DEFAULT are **angular accelerations** (rad/s²)
 * and **rate-damping coefficients** (1/s), not torques. Read as torques against the given
 * inertia tensor they would produce a roll time constant of I/K = 42/2.4 ≈ 17 s, which is not
 * an aircraft. Read as accelerations they give a steady-state roll rate of P/K = 1.92 rad/s
 * (110°/s) reached in 1/K = 0.42 s — which is exactly a small fast drone. The inertia tensor is
 * still used, for the Euler cross-coupling terms that make a fast roll pitch and yaw.
 */
export const FLIGHT = {
  // Post-stall lift decay. Gradual by construction: a cliff here reads as a bug, not a mistake.
  postStallRangeDeg: 12, // decay band above alphaStall
  postStallClFactor: 0.45, // fraction of clMax reached at alphaStall + postStallRangeDeg
  deepStallAlphaDeg: 45,
  deepStallClFactor: 0.15,
  separatedDragCd: 1.15, // extra CD at 90° of fully separated flow
  separationOnsetFraction: 0.7, // separation starts at this fraction of the stall angle
  separationFullFraction: 1.3, // and is complete by this one

  // Control authority = clamp(q / q_cruise, min, max). The reason slow flight is mushy.
  authorityMin: 0.15,
  authorityMax: 1.4,

  // Directional stiffness. Sized so a steep level turn needs only a boot of rudder to
  // coordinate: with yawDamp = 2.2 opposing the turn's yaw rate, a weaker fin would trim out at
  // several degrees of steady sideslip and every turn would visibly skid.
  weathervaneGain: 5.0, // rad/s² of yaw per radian of sideslip, at unit authority

  /**
   * Static stability in pitch and roll. **These two terms are an addition to the equations in
   * SPEC §3.2.3**, which give the yaw axis a weathervane term but leave the other two with
   * damping only. Without them the aircraft is neutrally stable in attitude, and three
   * consequences follow that all read as bugs:
   *
   *  - Turbulence integrates. Every gust permanently changes the attitude, so "flies stably
   *    hands-off in trim" is unachievable in the moving air the same spec section requires.
   *  - A deep stall is a trap. At 90° of α there is no nose-down moment, so the aircraft
   *    descends vertically, nose high, until the player pushes.
   *  - There is no speed stability, so the airframe has no natural cruise to return to.
   *
   * Pitch restores toward `state.trimAlphaRad` — a trim setting, not a fixed angle — so the
   * aircraft holds whatever it was trimmed for. Roll restores through the dihedral effect: a
   * dropped wing produces sideslip, and sideslip rolls the wing back up.
   */
  pitchStiffness: 3.0, // rad/s² of nose-down per radian of α above trim, at unit authority
  dihedralGain: 1.6, // rad/s² of roll per radian of sideslip, at unit authority
  adverseYawGain: 0.24, // rad/s² of opposing yaw per unit of roll command

  // Stall departure and autorotation (SPEC §3.2.3).
  autorotationGain: 1.35, // pro-roll rad/s² per (rad/s of roll rate · stall depth)
  autorotationSeedRadS: 0.12, // asymmetry that starts a departure from wings-level
  stallDampLoss: 0.8, // fraction of roll damping lost at full stall depth
  spinYawCoupling: 0.45, // yaw rate driven by roll rate in a developed spin
  stallControlLoss: 0.55, // fraction of pitch/roll authority lost at full stall depth

  // Limits and damage (SPEC §3.2.4).
  overGIntegrityPerGS: 0.055, // integrity lost per g beyond the limit, per second
  // Integrity lost per m/s beyond V_NE, per second. Sized so a 20 m/s overspeed takes most of a
  // minute to write the airframe off: long enough that the player can see the warning, react,
  // and learn, rather than losing the aircraft before the readout has updated.
  overSpeedIntegrityPerMsS: 0.0012,
  buffetGainRadS2: 1.1, // angular perturbation at 20 m/s past V_NE
  buffetReferenceMs: 20,
  damageControlLoss: 0.5, // control authority lost at full control damage

  // Numerical guards.
  minAeroAirspeedMs: 0.5, // below this the aerodynamic angles are undefined and forces vanish
  /**
   * Points sampled along each tick's motion segment for terrain collision (SPEC §4.4). At 90 m/s
   * a 120 Hz tick covers 0.75 m, so this is ample — but testing only the end point would let a
   * fast dive step straight through a ridge crest between ticks.
   */
  groundSweepSamples: 4,
  maxAlphaRad: Math.PI / 2,
} as const;

/** Atmosphere/wind defaults until missions supply their own (SPEC §3.2.5). */
export const WIND = {
  defaultMs: [2.5, 0, -1.5] as [number, number, number],
  gustAmplitudeMs: 1.6, // translational gust, added to the constant wind
  gustFrequencyHz: 0.09,
  /**
   * Peak angular perturbation from turbulence, rad/s², before ATMO.TURB_SCALE and the altitude
   * factor. Sized against the airframe's roll damping (2.4/s): at this amplitude a sustained
   * gust commands roughly 1°/s of roll, which the eye reads as air. An order of magnitude more
   * and turbulence stops being a disturbance and starts being the pilot — the aircraft wanders
   * off attitude on its own and flies itself into the ground unattended.
   */
  turbulenceAmplitudeRadS2: 0.25,
  turbulenceOctaveGain: 0.5, // amplitude of the second octave relative to the first
  turbulenceOctaveFrequency: 4.5,
} as const;

/**
 * Autopilot assist gains (SPEC §3.2.6). Cascaded loops: altitude → vertical speed → pitch
 * attitude → pitch command, and heading → bank → roll command. Assists disengage silently on
 * player input and must be re-armed by hand — automatic re-engagement mid-manoeuvre is the
 * single worst behaviour available here.
 */
export const AUTOPILOT = {
  inputBreakoutThreshold: 0.08, // control deflection that counts as the player taking over
  bankDisengageDeg: 45, // altitude hold gives up past this bank

  altitudeToClimbRate: 0.35, // (m/s) of commanded climb per metre of altitude error
  maxClimbRateMs: 12,

  /**
   * Angle-of-attack protection for the climb command, degrees.
   *
   * `maxClimbRateMs` bounds the *rate* the autopilot asks for but says nothing about whether the
   * engine can pay for it. Ask a 620 N airframe for 12 m/s at cruise and it climbs on stored
   * airspeed, decelerating the whole way, until it stalls at the top — which it then cannot
   * recover from. Alpha is the right signal because it measures stall proximity directly,
   * independently of speed, load factor and air density, which is why real limiters use it.
   *
   * Climb command tapers to zero as alpha crosses from `alphaLimitDeg - alphaTaperDeg` to
   * `alphaLimitDeg`. Set below the 14° `alphaStallDeg` so the taper is complete before the wing
   * is anywhere near letting go. Descent is never limited: pointing the nose down is always a
   * valid answer to being slow.
   */
  alphaLimitDeg: 11,
  alphaTaperDeg: 3,

  /**
   * Airspeed floor for the climb command, m/s.
   *
   * Alpha alone is too late. A fast aircraft asked for an unsustainable climb spends thirty
   * seconds converting speed into height at a perfectly ordinary alpha, and only when the speed
   * is gone does alpha rise — by which point the aircraft is behind the power curve at 39 m/s,
   * mushing, and full throttle will not recover it. Speed is the leading indicator of that
   * trade; alpha is the lagging one. Both are kept: alpha still guards the cases speed cannot
   * see, such as a climb commanded under load or in thin air.
   *
   * Climb tapers to zero between `climbSpeedFloorMs` and `climbSpeedFloorMs + climbSpeedTaperMs`,
   * holding the aircraft on the front side of the power curve where thrust still buys height.
   */
  climbSpeedFloorMs: 58,
  climbSpeedTaperMs: 14,
  climbRateToPitch: 0.06, // radians of pitch attitude per (m/s) of climb-rate error
  maxPitchDeg: 22,
  pitchToCommand: 3.4,
  pitchRateDamping: 0.9,

  headingToBank: 2.2, // radians of bank per radian of heading error
  maxBankDeg: 35,
  bankToCommand: 2.6,
  rollRateDamping: 0.55,

  recoverAltitudeM: 200, // AUTO-RECOVER trigger height (training difficulty only)
  recoverBankDeg: 90,
} as const;

/** Input shaping (SPEC §3.13). Degrees and pixels here; converted at the edge. */
export const INPUT = {
  // Keyboard: digital keys ramp toward full deflection rather than stepping to it.
  keyRampPerS: 3.2,
  keyReturnPerS: 4.5,
  throttleRatePerS: 0.45,

  // Mouse virtual joystick. Direct 1:1 mouse-to-surface mapping is unflyable and is not used.
  mouseSensitivity: 0.0022, // deflection per pixel of movement, pointer-locked
  mouseDeadzone: 0.06, // of the half-screen, unlocked fallback
  mouseRecentrePerS: 0.9, // rate the virtual stick eases back to centre when idle
  mouseMaxDeflection: 1,

  // Gamepad. Expo is what makes one stick serve both gentle corrections and a last-ditch break.
  padDeadzone: 0.15,
  padExpo: 0.35,
  padTriggerDeadzone: 0.08,
  padPollHz: 120,
} as const;

/**
 * The impact replay (SPEC §3.3.1). The single permitted exception to the nose-camera rule, and
 * only because it runs after the outcome is decided and grants no advantage.
 */
export const IMPACT_CAM = {
  durationS: 2.5,
  /** Slow motion: the last two seconds are worth seeing properly. */
  timeScale: 0.55,
  radiusM: 34,
  heightM: 12,
  orbitRateRadS: 0.35,
} as const;

export const CAMERA = {
  fovH: 78, // deg — horizontal field of view, converted to vertical at the aspect boundary
  tiltDownDeg: 4,
  nearM: 0.4,
  farM: 30000,
  lookConeYawDeg: 120,
  lookConePitchDeg: 60,

  // Look-around (SPEC §3.3.1). A concession to the loss of peripheral vision, defensible as a
  // "multi-camera drone" fiction, and disabled on Realistic difficulty.
  lookRateRadPerPixel: 0.0032,
  lookReturnPerS: 6.0, // snap-back rate on release
  lookPadRateRadPerS: 2.2,

  // Zoom is a crop, not a lens change: the FOV narrows and nothing else moves.
  zoomFovH: 34,
  zoomRatePerS: 4.0,
} as const;

/**
 * HUD appearance and geometry (SPEC §3.4). Pixel values are quoted at 1080p and scaled with
 * resolution at draw time; degrees here, radians inside the elements.
 */
export const HUD = {
  colour: '#3FE8D8',
  opacity: 0.85,
  lineWidth: 1.5,
  designationColour: '#FF3B30',
  // §3.4 asks for one colour and no others; the element table then calls for amber and red
  // warning states on airspeed, altitude and load factor. The specific rule wins — but these
  // three are the only exceptions, and they only ever appear when something is wrong.
  warnColour: '#FFB000',
  dangerColour: '#FF3B30',
  stallWarnFactor: 1.2, // airspeed turns amber below V_s(n) · this
  // Airspeed turns amber above V_NE · this, red at V_NE itself. 0.95 gives ~6 m/s of warning at
  // the scout's 115 m/s V_NE — a second or two of notice in a dive, which is the point.
  overspeedWarnFactor: 0.95,
  horizonGapPx: 90,
  ladderStepDeg: 5,
  reticleHeightPx: 26,
  fpmSizePx: 18,
  lowAltWarnM: 40,
  criticalAltM: 20,
  gWarn: 5.0,

  // --- Type
  fontPx: 11,
  letterSpacingPx: 1.1,
  fontFamily: '"SF Mono", "Roboto Mono", Menlo, Consolas, ui-monospace, monospace',

  // --- Horizon and ladder
  horizonBarLengthPx: 150,
  horizonTipDropPx: 8, // short downward tick at the inner end of each bar, by the centre gap
  ladderRungHalfWidthPx: 88,
  tagEdgeInsetFactor: 2.4, // keeps clamped bearing tags out of the readout columns
  ladderRungGapPx: 74, // gap in the middle of each rung, where the ladder passes the reticle
  ladderDashPx: 9,
  ladderEndTickPx: 7,
  ladderLabelOffsetPx: 10,
  ladderMaxPitchDeg: 90,

  // --- Symbology
  fpmWingPx: 9,
  fpmStemPx: 6,
  designationLengthPx: 10,

  // --- Readouts
  marginPx: 44,
  readoutGapPx: 16,
  /** Third row of a readout column, in units of `readoutGapPx` below the number: g, and throttle. */
  readoutRowGapFactor: 2.6,
  /** Indent of the throttle value from its `THR` label, quoted at 1080p. */
  throttleValuePx: 34,
  headingTapeWidthPx: 300,
  headingTapeTickPx: 6,
  headingTapeDegPerPx: 0.36,
  loadFactorShowAbove: 2.0,

  // --- Start hold (the mission-start control lock)
  /**
   * Countdown text, measured down from the top edge, quoted at 1080p.
   *
   * High rather than near the reticle. The hold runs while the aircraft is level and trimmed,
   * which is exactly when the horizon — and every bearing tag anchored to it — sits across the
   * middle of the display; and it runs during the opening radio call, which owns the low centre.
   * This band, under the heading tape and beside the tasking block, is the one that is always free.
   */
  startHoldTopPx: 150,

  // --- RWR (SPEC §3.4.1: 120 px, aircraft-relative, rotates with heading not with bank)
  rwrDiameterPx: 120,
  rwrTickPx: 9,
  rwrBottomMarginPx: 26,
  rwrLaunchFlashHz: 4,

  // --- Missile warning
  missileCaretPx: 18,
  missileTickPx: 7,
  missileClosureRefMs: 400, // closure at which the tick rate saturates
  missileTickMinHz: 1.2,
  missileTickMaxHz: 9,

  // --- Link quality
  linkBars: 4,
  linkBarWidthPx: 7,
  linkBarStepPx: 5,
  linkBarBasePx: 6,
} as const;

/**
 * HUD degradation under EW jamming (SPEC §3.4.2). The HUD is never simply hidden: it fails in
 * specific, informative ways, so the player learns to fall back on the horizon and the terrain.
 * Live from M5; the machinery and the test-page slider exist now.
 */
export const HUD_DEGRADATION = {
  tagDriftStart: 0.15, // jamming level at which bearing tags begin to wander
  tagDriftMaxDeg: 7,
  tagDropStart: 0.6, // and at which they start dropping out entirely
  headingUnreliableAt: 0.35,
  headingDriftMaxDeg: 14,
  fpmJitterMaxPx: 6,
  driftFrequencyHz: 0.21,
  jitterFrequencyHz: 7.5,
} as const;

/**
 * High-contrast accessibility mode (M2 acceptance). Thin cyan at 85% over a washed-out video
 * feed is exactly the case where a low-vision player loses the instrument, so the alternative
 * raises opacity to full, thickens the strokes, and lays a dark outline under every line so the
 * HUD stays readable against a bright sky.
 */
export const HUD_THEME = {
  standard: {
    colour: '#3FE8D8',
    opacity: 0.85,
    lineWidthScale: 1,
    fontScale: 1,
    outline: false,
    outlineColour: 'rgba(0, 0, 0, 0)',
    outlineWidthScale: 0,
  },
  highContrast: {
    colour: '#B8FFF4',
    opacity: 1,
    lineWidthScale: 1.6,
    fontScale: 1.18,
    outline: true,
    outlineColour: 'rgba(0, 12, 16, 0.85)',
    outlineWidthScale: 3.2,
  },
} as const;

export const DETECTION = {
  horizonK: 4.12, // d_km = K·(√h1_m + √h2_m)
  losSteps: 64,
  losCacheS: 1.0,
  /**
   * Terrain must rise this far above the sight line to block it, metres. Without a margin, a
   * radar standing on a hillside blocks its own view along that hillside: the line between two
   * points on a slope lies *on* the slope, and bilinear round-off decides the comparison.
   */
  losClearanceM: 0.5,
  /**
   * Quantisation of the target position used as the LOS cache key, metres. At 90 m/s the
   * aircraft leaves a 120 m cell about every 1.3 s, so the march runs at roughly the cache's
   * one-second lifetime whether the aircraft is manoeuvring or not.
   */
  losCacheCellM: 120,
  rcsAspectNose: 0.6,
  rcsAspectBeam: 1.0,
  notchVelocityMs: 22, // |radial velocity| below this ⇒ clutter rejection
  notchSoftnessMs: 8, // smooth band, so the notch is findable not binary
  /**
   * Doppler visibility a site needs to hold a return, 0–1. At the midpoint of the soft band, so
   * the detection edge sits exactly on `notchVelocityMs` and the constant keeps its meaning.
   */
  notchDetectThreshold: 0.5,
} as const;

/**
 * Radar warning receiver behaviour (SPEC §3.4.1).
 *
 * The RWR is a *receiver*: it shows emitters the aircraft can hear, which is not the same as
 * sites that have detected the aircraft. Hearing a search radar sweep past you while it fails to
 * hold a track is the feedback the whole notch mechanic is taught with.
 */
export const RWR = {
  /** Emissions are audible out to this multiple of the emitter's own search range. */
  hearingRangeFactor: 1.25,
  /** Below this Doppler visibility a tracking site reads as SEARCH again on the ring. */
  trackVisibilityFloor: 0.35,
} as const;

/**
 * Air defence site behaviour not covered by the per-threat catalogue (SPEC §3.5.2).
 * The catalogue itself lives in config/threats.ts.
 */
export const SITE = {
  /**
   * Crew quality scales acquire and reaction times by `2 − quality`: a perfect crew works to the
   * catalogue figure, a poor one takes half as long again. Bounded at both ends on purpose —
   * a crew that reacted instantly would give the player no window at all.
   */
  crewQualityFloor: 0.25,
  /**
   * Hysteresis on the search-range wake-up, so a site sitting exactly at the edge does not
   * chatter between DORMANT and SEARCHING at the detection rate.
   */
  wakeHysteresis: 1.1,
  /**
   * How long a woken site keeps sweeping after the target has left its search range entirely.
   *
   * **This edge is an addition to the state machine of SPEC §3.5.2**, which has no way back to
   * DORMANT. Without it every site the player ever flew past stays lit on the RWR for the rest of
   * the mission, and the instrument becomes a list of places they have been rather than a picture
   * of what is looking at them now.
   */
  standDownS: 12,
  /**
   * How long GUIDING coasts before falling back when no missile event arrives. At M6 the missile
   * itself reports detonation or expiry and this becomes a backstop rather than the mechanism.
   */
  guidanceTimeoutS: 30,
} as const;

export const SHORAD = {
  searchRangeM: 28000,
  trackRangeM: 18000,
  minEngageM: 1000,
  maxEngageM: 12000,
  minAltM: 15,
  maxAltM: 8000,
  acquireTimeS: 2.5,
  reactionTimeS: 2.0,
  trackMemoryS: 4.0,
  salvoSize: 2,
  salvoIntervalS: 1.5,
  reloadS: 12,
  magazine: 12,
  radarMastHeightM: 6,
  crewQuality: 0.7,
} as const;

export const MISSILE_SHORAD = {
  massKg: 75,
  diameterM: 0.17,
  boostThrustN: 26000,
  boostDurationS: 2.6,
  sustainThrustN: 0,
  sustainDurationS: 0,
  cd0: 0.42,
  inducedDragK: 0.0021, // per (m/s²)² of commanded lateral accel
  maxG: 28,
  navConstant: 3.5,
  seeker: 'radar-command',
  seekerFovDeg: 30,
  seekerLockRangeM: 20000,
  maxFlightTimeS: 26,
  minSpeedMs: 180,
  fuseRadiusM: 10,
  warheadYield: 1.0,
  chaffSusceptibility: 0.45,
  flareSusceptibility: 0.0,
} as const;

export const MISSILE_MANPADS = {
  massKg: 11,
  diameterM: 0.072,
  boostThrustN: 4200,
  boostDurationS: 1.9,
  // A shoulder-launched round is all boost: it coasts from burnout, which is why denying it the
  // rear aspect early matters so much more than out-turning it late.
  sustainThrustN: 0,
  sustainDurationS: 0,
  cd0: 0.38,
  inducedDragK: 0.0034,
  maxG: 18,
  navConstant: 3.0,
  seeker: 'ir',
  seekerFovDeg: 25,
  seekerLockRangeM: 5000,
  maxFlightTimeS: 14,
  minSpeedMs: 140,
  fuseRadiusM: 6,
  warheadYield: 0.45,
  chaffSusceptibility: 0.0,
  flareSusceptibility: 0.6,
  rearAspectBias: 0.7, // lock probability multiplier off rear aspect
} as const;

/**
 * Interceptor drone (SPEC §3.5.1, M7). Faster than the player and better armed, and beatable only
 * by making it spend energy it cannot afford — so every number here exists to make that trade
 * legible rather than to make the aircraft good.
 */
export const INTERCEPTOR = {
  cruiseSpeedMs: 125,
  /**
   * Speed below which the wing can no longer pull the limit load. The whole energy fight is
   * about dragging the interceptor beneath this and keeping it there.
   */
  cornerSpeedMs: 105,
  /** Speed at which it abandons the turn rather than depart — the window the player creates. */
  stallGuardSpeedMs: 62,
  /** And the speed it needs back before it will fight again. */
  recoverToSpeedMs: 95,
  maxG: 7.5,
  /** Floor on available g, so a slow interceptor still flies rather than falling. */
  minG: 1.2,
  navConstant: 3.2,
  thrustMs2: 9.0,
  /** Downward acceleration commanded while unloading to regain speed. */
  recoverDescentMs2: 5.5,
  /** Quadratic drag: m/s² per (m/s)². Sized so a sustained hard turn bleeds it in a few seconds. */
  dragPerSpeedSq: 0.00055,
  commitRangeM: 6000,
  mergeRangeM: 900,
  terrainClearanceM: 120,
} as const;

export const COUNTERMEASURES = {
  chaffCount: 12,
  chaffBloomS: 3.0,
  chaffRadiusM: 45,
  flareCount: 16,
  flareBurnS: 4.0,
  flareIrGain: 3.2,
  /**
   * Aspect change required after release before a flare can take the lock, degrees.
   *
   * Appendix A starts this at 25°, which this airframe can never reach: at 90 m/s the aircraft
   * opens about 14° in the two seconds during which the flare is still the brighter source, and
   * 25° would need roughly ten seconds against a four-second burn — so flares would never work at
   * all, in any geometry, which is not what SPEC §3.6.3 describes. 12° preserves the rule that
   * matters (flying straight opens only 2–3° and defeats nothing) while leaving the punch-and-turn
   * the spec requires actually achievable.
   */
  flareTurnRequiredDeg: 12,
} as const;

export const EW = {
  jammerRadiusM: 25000,
  /**
   * Falloff exponent of jamming strength with normalised distance from the emitter. Above 1 the
   * zone has a soft edge and a hard core, which is what makes routing around the fringe of a
   * jammer a real decision rather than a binary in-or-out.
   */
  jammerFalloffExp: 1.8,
  /** Link quality remaining at the centre of a jammer zone. */
  jammedLinkFloor: 0.05,
  /** Rate the accumulated GPS error decays once clear of jamming, m/s. */
  gpsRecoveryMs: 40,
  /** Direction the GPS error wanders, revolutions per second. Slow, so the drift is not a wobble. */
  gpsDriftTurnHz: 0.037,
  /** Control station antenna height above the terrain, metres. */
  stationMastHeightM: 12,
  /** Link quality multiplier when terrain masks the line to the control station. */
  occludedLinkFactor: 0.45,
  gpsDriftMaxMs: 15,
  latencyMaxS: 1.2,
  linkLossThreshold: 0.08,
  linkLossMaxS: 4.0,
} as const;

export const DAMAGE = {
  falloffExp: 1.6,
  controlLossThreshold: 0.45,
  engineLossThreshold: 0.7,
  structuralThreshold: 0.9,
} as const;

export const IMPACT = {
  directHitRadiusM: 2.0,
  hitRadiusM: 8.0,
  nearMissRadiusM: 25.0,
  minEffectiveSpeedMs: 45,
  /** Falloff exponent with distance from the aim point (SPEC §3.8.2). */
  falloffExp: 1.4,
  /**
   * Effect at or above which an attack satisfies its objective — the boundary between NEAR MISS
   * and HIT. Below half, the target is degraded but still in business, which is the outcome that
   * makes a second pass worth flying.
   */
  hitEffect: 0.5,
} as const;

/**
 * Airframe battle damage (SPEC §3.8, M7). Damage is not one number: which *system* is hit decides
 * how the aircraft flies afterwards, and an asymmetric control loss has to actually make it fly
 * crooked rather than merely fly worse.
 */
export const AIRFRAME_DAMAGE = {
  /**
   * Roll acceleration bias at a total loss of one aileron, rad/s². Held opposite stick is the
   * only thing that flies straight afterwards — which is the point: SPEC's M7 note requires the
   * asymmetry to be felt, not reported.
   */
  asymmetricRollRadS2: 1.35,
  /** Yaw bias at a total loss of one side of the tail, rad/s². */
  asymmetricYawRadS2: 0.5,
  /** Fraction of an axis's authority lost at full damage to that surface. */
  surfaceAuthorityLoss: 0.75,
  /** Damage above which a detonation may take out a control surface rather than spread evenly. */
  surfaceHitThreshold: 0.06,
  /** Thrust remaining at full engine damage, before the engine quits outright. */
  engineThrustFloor: 0.35,
  /** Fuel leak rate at full tank damage, kg/s. */
  fuelLeakKgS: 0.4,
} as const;

export const WORLD = {
  sizeM: 24000,
  heightmapRes: 2048,
  maxAltitudeM: 1400,
  chunkSizeM: 3000,
  lodLevels: 5,
  scatterSeed: 0x5ea51de,
} as const;

export const RENDER = {
  targetFrameMs: 15.0,
  dynResMin: 0.6,
  dynResMax: 1.0,
  dynResStep: 0.05,
  dynResCooldownFrames: 30,
  shadowCascades: 2,
  shadowMapSize: 2048,
  shadowDistanceM: 400,
  maxPixelRatio: 2, // cap devicePixelRatio; retina at scale 1.0 is not worth 4× the pixels
} as const;

/**
 * Video downlink post-processing (SPEC §3.3.2). Pixel figures are quoted at 1080p and scaled at
 * draw time; normalised radii run 0 at the frame centre to 1 at a corner.
 *
 * Two of the ten effects carry gameplay signal and are therefore tuned as game systems rather
 * than as looks: auto-exposure (item 3), because a sun-facing ingress must genuinely hide
 * threats, and compression blocking (item 6), because a degrading picture is the only warning
 * that the link is being jammed.
 */
export const POST = {
  // --- 1–2. Lens: barrel distortion and chromatic aberration
  barrelK1: 0.12, // r' = r·(1 + k1·r²), matching a wide-angle action-camera lens
  /** Radial per-channel separation at a corner, px at 1080p. Falls to zero at the centre. */
  chromaticPx: 2.4,

  // --- 3. Exposure / auto-gain
  /** Adaptation lag, seconds — the time constant of the exponential approach (SPEC §3.3.2). */
  exposureAdaptS: 0.6,
  /**
   * Log-average scene luminance the gain drives toward.
   *
   * Calibrated against a normally-lit theatre — mid-morning, coast ahead, sea below — so that
   * flight sits at 0 EV and the gain only moves when the camera is pointed somewhere genuinely
   * bright or genuinely dark. Set it lower and every frame is stopped down for no reason.
   */
  exposureTargetLuminance: 0.3,
  exposureMinStops: -2.5,
  exposureMaxStops: 2.5,
  /** Metering rate, Hz. The 0.6 s lag makes anything faster invisible, and each sample stalls. */
  exposureSampleHz: 15,
  /** Metering grid, texels per side. Downsampled twice: scene → this → 1×1. */
  meterResolution: 16,
  /** Centre-weighting of the meter: the middle of the frame counts this many times the edge. */
  meterCentreWeight: 2.4,
  /** Log-luminance range the metering byte encodes, stops. Outside it the meter saturates. */
  meterEncodeMinStops: -12,
  meterEncodeMaxStops: 6,
  /**
   * Highlight shoulder knee. Below it the response is linear, above it everything rolls
   * asymptotically into white, so the sun blooms out instead of clipping to a flat disc. A curve
   * that bent the mid-tones too would darken the whole picture for no reason and read as a
   * lighting bug rather than as a camera.
   */
  tonemapKnee: 0.72,

  // --- 4. Colour grade: the reference's washed teal-amber
  gradeSaturation: 0.75,
  gradeContrast: 0.84, // <1 crushes contrast
  gradeLift: 0.05, // lifted blacks
  gradePivot: 0.42,
  /** Multiplicative tints, cyan in the shadows and amber in the highlights. */
  gradeShadowTint: [0.72, 1.04, 1.1] as [number, number, number],
  gradeHighlightTint: [1.08, 0.97, 0.8] as [number, number, number],
  gradeTintStrength: 0.55,

  // --- 5. Atmospheric haze (the camera-side veil; see PostStack for the split with world fog)
  /** Veil at full optical depth, before the sun glare is added. */
  hazeVeilStrength: 0.35,
  /** Extinction coefficient, per metre. The veil is exponential-squared in optical depth. */
  hazeDensity: 0.00004,
  /** Path length assigned to a ray that never meets the sea, metres — the far plane, in effect. */
  hazeMaxRangeM: 30000,
  /** Forward-scatter glare toward the sun, and the Henyey–Greenstein anisotropy that shapes it. */
  hazeSunGlare: 0.55,
  hazeSunAnisotropy: 0.7,
  /** Multiplier on the veil when the aircraft is over water, where the air is wetter. */
  hazeOverWaterBoost: 1.6,

  // --- 6. Compression blocking, driven by link quality
  blockSizePx: 8,
  /** Link quality above which the picture is clean; below it, blocking ramps in. */
  blockOnsetLink: 0.72,
  /** Fraction of macroblocks that have collapsed to their DC term at zero link. */
  blockMaxCoverage: 0.85,
  /** Quantisation levels per channel at full link and at zero link. */
  quantLevelsClean: 255,
  quantLevelsWorst: 6,

  // --- 7. Frame jitter, hold and tear
  /** Downlink frame rate, Hz. Hold and tear decisions are made on this clock, not the render one. */
  linkFrameHz: 30,
  jitterOnsetLink: 0.55,
  /** Probability that a downlink frame is repeated rather than refreshed, at zero link. */
  holdMaxProbability: 0.42,
  tearOnsetLink: 0.34,
  tearMaxProbability: 0.3,
  /** Horizontal displacement of a torn band, fraction of frame width. */
  tearMaxShift: 0.05,
  /** Whole-frame positional jitter at zero link, fraction of frame width. */
  jitterMaxOffset: 0.006,

  // --- 8. Rolling shutter
  /** Sensor readout time top-to-bottom, seconds. Skew = yaw rate · this, in radians of view. */
  rollingShutterS: 0.014,

  // --- 9. Sensor grain
  grainAmplitude: 0.03,
  /** Extra grain in the shadows, where a real sensor's gain is doing the most work. */
  grainShadowGain: 1.6,
  /** Grain resample rate, Hz. Per-frame grain crawls; a fixed rate reads as film. */
  grainRateHz: 24,

  // --- 10. Vignette
  vignetteStrength: 0.34,
  /** Normalised radius at which the falloff starts. */
  vignetteStart: 0.5,
} as const;

/**
 * Transient effects (SPEC §5.3). The smoke trail carries gameplay signal — it is how a launch is
 * seen before the RWR reports it — so its numbers are sized for legibility at range, not for looks.
 */
export const EFFECTS = {
  /** True ribbon half-width at the missile, metres. */
  trailWidthM: 1.6,
  /**
   * Minimum ribbon half-width as a fraction of the view depth — a screen-space floor. At 8 km
   * this holds the trail at roughly four pixels of a 1080p frame instead of a third of one.
   */
  trailMinScreenFraction: 0.0016,
  trailOpacity: 0.72,
  smokeColour: 0xd8dee2,

  flashColour: 0xffd9a0,
  flashSizeM: 26,
  flashLifetimeS: 0.5,
  maxFlashes: 24,

  flareColour: 0xffb057,
  decoySizeM: 6,
  maxDecoys: 64,

  // --- Explosions (SPEC §5.3): layered flash, smoke ball, debris, dust ring, shock ring.
  /** Particles in one explosion's smoke ball and debris shower. */
  explosionSmoke: 26,
  explosionDebris: 18,
  explosionLifetimeS: 2.6,
  debrisLifetimeS: 3.4,
  debrisSpeedMs: 34,
  debrisSizeM: 1.4,
  smokeBallSizeM: 9,
  smokeRiseMs: 4.5,
  smokeColourHot: 0x50483f,

  // --- Sustained secondaries: a fuel target burns for half a minute (SPEC §5.3).
  fireParticles: 34,
  fireColumnHeightM: 34,
  fireRiseMs: 9,
  fireSizeM: 7,
  fireColour: 0xff8a2a,
  fireSmokeColour: 0x2b2724,
  /** Particles emitted per second while a fire burns. */
  fireEmitPerS: 14,

  // --- Water impact: column plume plus an expanding ring wave.
  plumeParticles: 22,
  plumeSpeedMs: 26,
  plumeSizeM: 6,
  plumeColour: 0xdfe8ea,

  // --- Damage smoke trailing from a wounded airframe.
  damageSmokeEmitPerS: 22,
  damageSmokeLifetimeS: 2.2,
  damageSmokeSizeM: 3.2,
  /** Damage above which the airframe starts trailing smoke. */
  damageSmokeThreshold: 0.15,

  /** Ground scorch decals left by an impact. */
  maxDecals: 24,
  decalRadiusM: 9,

  /** Ceiling on live particles across every system — the M7 budget is 2 ms at peak. */
  maxParticles: 900,
} as const;

// --- Engine-side limits (not gameplay, but no magic numbers in logic files either) ------------

export const ENGINE = {
  MAX_ENTITIES: 4096, // registry capacity; entity index is packed into 20 bits
  ENTITY_INDEX_BITS: 20,
  ENTITY_GENERATION_BITS: 12,
} as const;

export const DEBUG = {
  FRAME_GRAPH_SAMPLES: 120, // rolling window of the frame-time graph
  GRAPH_WIDTH_PX: 240,
  GRAPH_HEIGHT_PX: 56,
  GRAPH_CEILING_MS: 33.4, // top of the graph = two dropped frames at 60 Hz
  BUDGET_LINE_MS: 16.6, // 60 fps budget marker
  TEXT_UPDATE_HZ: 5, // throttle DOM text writes; string building allocates
  TIME_SCALES: [1, 0.5, 0.25, 0.1] as const, // cycled by the T key; 0.1× per SPEC §4.6
  SELFTEST_TICKS: 1200, // 10 s of simulated time compared tick-by-tick
} as const;

/** Sky, sun, and haze (SPEC §3.9.3). Hours and degrees here; radians inside Sky.ts. */
export const SKY = {
  defaultTimeOfDayHours: 9.5,
  sunriseHours: 6,
  sunsetHours: 18.5,
  maxSunElevationDeg: 58,
  sunriseAzimuthDeg: 82, // just north of due east
  sunsetAzimuthDeg: 278,
  daySunIntensity: 1,
  nightSunIntensity: 0.03,
  sunLightIntensity: 2.6,
  ambientIntensity: 1.1,
  /**
   * Exponential-squared haze density. Also the draw-distance mechanism (SPEC §3.3.2 item 5):
   * tuned so a 1300 m ridge is still a legible silhouette at 10 km and gone by 20.
   */
  hazeDensity: 0.000042,
  domeRadiusM: 1,
} as const;

/**
 * Terrain rendering (SPEC §3.9.1). Continuous distance-dependent LOD: one instanced grid patch,
 * a quadtree selection on the CPU, and vertex-shader morphing between levels.
 */
export const TERRAIN_RENDER = {
  /** Vertices per side of the shared patch mesh. One less than a power of two plus one. */
  patchVertices: 33,
  /** Quadtree depth below the 3 km root chunks; 5 levels total, per SPEC §3.9.1. */
  lodLevels: 5,
  rootChunkM: 3000,
  /** Distance within which the finest level is used; each coarser level doubles it. */
  baseLodRangeM: 620,
  /** Fraction of a level's range at which morphing to the parent begins. */
  morphStartRatio: 0.68,
  maxPatches: 768,
  /** Detail texture tiling, metres per repeat. */
  detailNearM: 18,
  detailFarM: 340,
} as const;

/**
 * Scatter and settlement (SPEC §3.9.2). Placement is seeded and rule-based — slope, altitude and
 * distance to water — so the same theatre grows the same forest on every machine.
 */
export const SCATTER = {
  cellSizeM: 200,
  treesPerCell: 12,
  treeHeightM: 9,
  treeHeightVariation: 0.42,
  /** Radius around the aircraft within which vegetation is instanced. */
  radiusM: 2600,
  /** Camera movement that triggers a rebuild of the instance buffers, metres. */
  rebuildThresholdM: 60,
  /** LOD 0 out to the first distance, LOD 1 to the second, billboards beyond (SPEC §5.1). */
  lodDistancesM: [420, 800] as [number, number],
  maxInstancesPerLod: 6000,

  // --- Placement rules
  maxSlopeDeg: 27,
  minAltitudeM: 6,
  maxAltitudeM: 620,
  /** Tree line: density falls to nothing across this band below maxAltitude. */
  altitudeFadeM: 160,

  // --- Settlement
  townBuildings: 240,
  buildingMaxSlopeDeg: 9,
  floorHeightM: 3.2,
  gablePitch: 0.34,
  parapetM: 0.7,
  buildingSpreadM: 1.35, // town radius multiplier the buildings spread over
} as const;

/** Ocean (SPEC §3.9.1). Three Gerstner trains on a camera-centred radial grid. */
export const OCEAN = {
  /** Radial grid: angular segments and rings out to the horizon. */
  radialSegments: 192,
  rings: 128,
  outerRadiusM: 26000,
  innerRadiusM: 6,
  /** Ring spacing grows as (i/N)^exponent, so detail concentrates near the aircraft. */
  ringExponent: 3.1,
  /** Wave trains: wavelength (m), amplitude (m), steepness, direction (deg from north). */
  waves: [
    { wavelengthM: 118, amplitudeM: 0.72, steepness: 0.55, directionDeg: 232 },
    { wavelengthM: 61, amplitudeM: 0.36, steepness: 0.62, directionDeg: 258 },
    { wavelengthM: 27, amplitudeM: 0.14, steepness: 0.7, directionDeg: 205 },
  ] as const,
  /** Displacement fades out beyond this range, where the grid can no longer resolve it. */
  displacementFadeM: 2600,
  shallowColour: [0.16, 0.42, 0.44] as const,
  deepColour: [0.014, 0.068, 0.11] as const,
  /** Depth over which the colour ramps from shallow to deep, metres. */
  depthRampM: 34,
  foamWidthM: 26,
  foamStrength: 0.85,
  /** Fresnel reflectance at normal incidence for water. */
  fresnelF0: 0.02,
  specularPower: 220,
} as const;

/**
 * Where the aircraft starts, until missions define spawns at M8. Six kilometres offshore on an
 * easterly run in: the coastline, the bay and the mountain spine all come up ahead, which is both
 * the best first view of the theatre and the ingress the missions are built around.
 */
export const SPAWN = {
  positionM: [-6200, 450, 2200] as [number, number, number],
  headingDeg: 90,
  airspeedMs: 78,
  throttle: 0.55,
} as const;

/**
 * Pooled breadcrumb trail dropped along the flight path. A debug instrument, not an effect:
 * seeing your own turn circle from inside a hard break is the fastest way to judge turn rate,
 * radius, and whether the aircraft is slipping. It also gives Pool a real consumer, so the
 * overlay's high-water mark means something. Replaced by the effects system at M7.
 */
export const TRAIL_MARKERS = {
  // Steady-state occupancy is lifetimeS / (spawnIntervalTicks · FIXED_DT); the pool is sized
  // above that so the high-water mark reports real demand rather than the ceiling.
  poolCapacity: 160,
  spawnIntervalTicks: 12, // one marker every 100 ms at 120 Hz
  lifetimeS: 12,
  sizeM: 1.4,
} as const;

/**
 * Mission system (SPEC §3.10).
 *
 * The 10 Hz evaluation rate is from the spec and is not a compromise: a zone test at 120 Hz costs
 * twelve times as much to answer the same question, and 100 ms of latency on a radio call is below
 * the threshold at which anyone notices.
 */
export const MISSION = {
  evaluateHz: 10,
  /** Default capture radius for a `reach` objective, metres. */
  waypointRadiusM: 250,
  /** Seconds a photograph target must be held in frame (SPEC §3.10 objective kinds). */
  photographHoldS: 2.5,
  /** Half-angle of the camera cone that counts as "in frame" for a photograph, degrees. */
  photographConeDeg: 12,
  /** Seconds after the terminal impact before the mission resolves, leaving room for the cam. */
  outcomeDelayS: 3.0,
  /**
   * Seconds of held controls at the start of a mission.
   *
   * The aircraft flies trimmed and level through it. Long enough that a keypress left over from
   * the briefing cannot roll the aircraft before the player is looking at it, short enough that a
   * player who knows the mission is not made to wait.
   */
  controlLockS: 5,
} as const;

/**
 * Score shaping (SPEC §3.12).
 *
 * Every threshold here is a statement about what the game rewards, so they are named rather than
 * inlined. The evasion band is the load-bearing one: it is what separates a break turn from
 * having simply been somewhere else.
 */
export const SCORING = {
  /** A missile defeated at or inside this miss distance scores full marks, metres. */
  evasionIdealM: 60,
  /** Beyond this it was never a threat, metres. */
  evasionLooseM: 500,
  /** Credit for a missile that was never close. */
  evasionLooseCredit: 0.35,
  /** Mean ingress AGL at or below which the altitude term saturates, metres. */
  idealIngressAltM: 120,
  /** Mean ingress AGL at which it scores nothing, metres. */
  poorIngressAltM: 900,
  /** Overrun of this many par-times takes the time term to zero. */
  timeOverrunTolerance: 1.0,
  proficientScore: 620,
  proficientObjectives: 1,
} as const;

/** Gauntlet escalation (SPEC §3.10.2). Difficulty is density and competence, never health bars. */
export const GAUNTLET = {
  seed: 0x5eed9a11,
  baseSites: 1,
  baseCrewQuality: 0.45,
  crewQualityPerWave: 0.045,
  baseMagazine: 4,
  wavesPerReload: 3,
} as const;

/** Where the radio subtitle sits above the RWR ring, CSS px from the bottom (SPEC §3.14). */
export const HUD_MISSION = { radioSubtitleBottomPx: 250 } as const;

/**
 * Audio (SPEC §3.11, §5.4). Everything synthesised; no sample files exist.
 *
 * The RWR numbers are the load-bearing ones. SPEC calls the RWR "the primary threat channel when
 * the picture is degraded", and the M9 note requires its tones to be distinguishable with eyes
 * closed and to survive a low effects mix — so the three tones are separated in pitch, rhythm and
 * timbre rather than in volume alone, and the bus is independent of effects.
 */
export const AUDIO = {
  masterGain: 0.6,
  effectsGain: 0.9,
  rwrGain: 0.85,

  /** Speed of sound for the distance delay on distant events, m/s. */
  speedOfSoundMs: 343,
  /** Beyond this, an event is inaudible and is not scheduled at all, metres. */
  maxAudibleM: 6000,
  /** Reference distance for the inverse falloff, metres. */
  referenceDistanceM: 60,
  /** Terrain occlusion attenuation, as a gain multiplier when the line of sight is blocked. */
  occlusionGain: 0.25,
  /** Low-pass cutoff applied to an occluded source, Hz — rock eats treble first. */
  occlusionCutoffHz: 700,

  // --- Engine (harmonic stack)
  engineBaseHz: 42,
  engineHarmonics: 5,
  /** Fundamental multiplier from idle to full throttle. */
  enginePitchRange: 2.1,
  /** Extra pitch from airspeed, per m/s, unitless multiplier. */
  enginePitchPerMs: 0.0016,
  engineGain: 0.16,
  engineFilterHz: 1400,
  /** Slow amplitude wobble so the note is not a pure tone. */
  engineRoughnessHz: 6.5,
  engineRoughnessDepth: 0.12,

  // --- Airflow and buffet
  airflowGain: 0.22,
  /** Noise cutoff at zero and at 140 m/s, Hz. */
  airflowCutoffMinHz: 200,
  airflowCutoffMaxHz: 5200,
  /** Buffet rides on stall depth: a free stall warning learned before the word (SPEC §3.11). */
  buffetGain: 0.5,
  buffetCentreHz: 90,
  buffetQ: 1.4,

  // --- RWR tones
  rwrSearchHz: 1250,
  rwrSearchChirpS: 0.06,
  rwrSearchIntervalS: 1.4,
  rwrTrackHz: 900,
  rwrTrackWarbleHz: 11,
  rwrTrackIntervalS: 0.42,
  rwrTrackChirpS: 0.1,
  rwrLaunchHz: 1650,
  /** The launch tone is continuous, not pulsed — that is what makes it unmistakable. */
  rwrLaunchGain: 0.5,

  // --- Events
  launchNoiseS: 1.6,
  launchCutoffHz: 900,
  detonationThumpHz: 55,
  detonationS: 1.1,
  impactS: 0.18,
  /** Missile flyby: the Doppler sweep of a near miss (SPEC §3.11). */
  flybyS: 0.7,
  flybyStartHz: 2100,
  flybyEndHz: 520,
  /** Airframe creak above this load factor. */
  stressGThreshold: 4,
  stressHz: 130,
  stressGain: 0.3,
} as const;

/** Flight recorder sizing (SPEC §3.15). 20 Hz for 12 minutes, with headroom. */
export const TELEMETRY = {
  /** Samples. 20 Hz × 15 min = 18 000; about 0.9 MB across twelve Float32 columns. */
  capacity: 18000,
  maxMarks: 2000,
  /** Ground-track decimation for the debrief map: one point per this many samples. */
  trackStride: 4,
} as const;

/**
 * Debrief judgement thresholds (SPEC §3.12, M10).
 *
 * These are the numbers behind the plain-language verdicts, so they are named rather than buried
 * in the prose that reports them. `lateBreakS` is the important one: it encodes the finding from
 * M6 that proportional navigation corrects any manoeuvre it has time for, so a break inside this
 * window cannot succeed on geometry alone.
 */
export const DEBRIEF = {
  /** Load factor that counts as a deliberate break turn rather than a course correction. */
  breakTurnG: 3.0,
  /** A break begun with less than this much missile flight time left is too late to work. */
  lateBreakS: 2.5,
  /** Miss distance at or under which a detonation is treated as a hit, metres. */
  hitDistanceM: 12,
  /** A debrief listing everything teaches nothing. */
  maxLessons: 4,
} as const;

/**
 * The briefing and debrief map (SPEC §3.14).
 *
 * Threat rings are drawn as rings because "the player's job is spatial" — and search and
 * engagement radii are separate rings because they answer different questions: where you are seen
 * versus where you are shot at. The gap between them is the route you can fly knowingly.
 */
export const MAP = {
  sizePx: 620,
  /** Metres across the map at default zoom. The theatre is 24 km. */
  defaultSpanM: 24000,
  centreXM: 0,
  centreZM: 0,
  seaLevelM: 0.5,
  shadeCeilingM: 1400,
  labelPx: 10,
  searchRingColour: 'rgba(255, 138, 96, 0.55)',
  engageRingColour: 'rgba(255, 92, 74, 0.9)',
  engageFillColour: 'rgba(255, 92, 74, 0.09)',
  routeColour: 'rgba(63, 232, 216, 0.9)',
  targetColour: 'rgba(255, 214, 102, 0.95)',
  trackColour: 'rgba(150, 235, 255, 0.95)',
  trackEndColour: 'rgba(255, 92, 74, 0.95)',
} as const;

/** Briefing map framing. Extends MAP above; kept adjacent for the same reason. */
export const MAP_FRAME = {
  /** Never zoom in tighter than this, metres across. */
  minSpanM: 7000,
  /** Multiplier on the mission's extent, leaving room for rings and labels. */
  framePadding: 1.9,
} as const;

/** How abruptly a particle fades out over its life. Above 1 it holds opacity then drops away. */
export const PARTICLE_FADE = { sharpness: 1.6 } as const;

/** Width of one monospace character as a fraction of font size, for HUD label collision tests. */
/**
 * World markers: the checkpoint ring and the target beacon.
 *
 * These are references built into the scene rather than symbology, so two rules hold them in
 * place. They are **depth-tested** — terrain hides them, because a beacon visible through a ridge
 * would undo the terrain masking that §3.5 makes the core of the game. And they are **not fogged**,
 * because a marker that dissolves into haze at eight kilometres fails at exactly the range it is
 * there for.
 *
 * Metres unless stated.
 */
export const MARKERS = {
  /** Segments per ring. Enough to read as a circle at close range, cheap enough to ignore. */
  ringSegments: 48,
  /** Inward tick length at the ring's cardinals, as a fraction of the radius. */
  tickFraction: 0.16,
  /** Shorter than this and the post is not drawn: a ring sitting on the ground needs no stalk. */
  minPostM: 15,

  checkpointColour: 0x3fe8d8,
  targetColour: 0xff3b30,
  /** Line opacity. Solid enough to see, faint enough not to be part of the terrain. */
  opacity: 0.55,
  /** Peak-to-trough of the slow pulse, and its period in seconds. Driven by simulated time. */
  pulseDepth: 0.35,
  pulsePeriodS: 2.4,

  /** Height of the target beacon column. Tall enough to clear the ridgelines around a target. */
  beaconHeightM: 140,
  beaconFootRadiusM: 18,
  /** The column is broken by a gap between these two fractions of its height. */
  beaconGapStart: 0.42,
  beaconGapEnd: 0.58,
  /** Half-length of the crossed bars at the top of the column. */
  beaconCapM: 12,
} as const;

export const HUD_TAGS = {
  charWidthFactor: 0.6,
  /** Below this a target's range reads in metres, at or above it in kilometres. */
  rangeKmThresholdM: 1000,
} as const;
